import { useState } from "react";
import { supabase } from "@/lib/supabase";

interface SubscriptionResult {
  success: boolean;
  error?: string;
}

interface UseNewsletterSubscriptionReturn {
  subscribe: (
    email: string
  ) => Promise<SubscriptionResult>;
  isLoading: boolean;
  isSuccess: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for handling newsletter email subscriptions with Supabase.
 * Stores subscriber emails in the `newsletter_subscribers` table.
 */
export function useNewsletterSubscription(): UseNewsletterSubscriptionReturn {
  const [isLoading, setIsLoading] =
    useState(false);
  const [isSuccess, setIsSuccess] =
    useState(false);
  const [error, setError] = useState<
    string | null
  >(null);

  const subscribe = async (
    email: string
  ): Promise<SubscriptionResult> => {
    setIsLoading(true);
    setError(null);
    setIsSuccess(false);

    try {
      // Validate environment variables
      if (
        !import.meta.env.VITE_SUPABASE_URL ||
        !import.meta.env.VITE_SUPABASE_ANON_KEY
      ) {
        throw new Error(
          "Configuration error. Please try again later."
        );
      }

      // Validate email format
      const emailRegex =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error(
          "Please enter a valid email address."
        );
      }

      // Get UTM parameters if available
      const urlParams = new URLSearchParams(
        window.location.search
      );
      const utmSource =
        urlParams.get("utm_source");
      const utmMedium =
        urlParams.get("utm_medium");
      const utmCampaign = urlParams.get(
        "utm_campaign"
      );

      // Submit to Supabase
      const { error: supabaseError } =
        await supabase
          .from("newsletter_subscribers")
          .insert([
            {
              email: email.toLowerCase().trim(),
              source: "landing_page_footer",
              utm_source: utmSource,
              utm_medium: utmMedium,
              utm_campaign: utmCampaign,
              subscribed_at:
                new Date().toISOString(),
              status: "active",
            },
          ]);

      if (supabaseError) {
        // Handle duplicate email
        if (supabaseError.code === "23505") {
          throw new Error(
            "This email is already subscribed."
          );
        }
        console.error(
          "Supabase error:",
          supabaseError
        );
        throw new Error(
          "Failed to subscribe. Please try again."
        );
      }

      // Send Slack notification
      const slackWebhookUrl = import.meta.env
        .VITE_SLACK_WEBHOOK_URL;
      if (slackWebhookUrl) {
        try {
          const slackPayload = {
            blocks: [
              {
                type: "header",
                text: {
                  type: "plain_text",
                  text: "📧 New Newsletter Subscriber!",
                  emoji: true,
                },
              },
              {
                type: "section",
                fields: [
                  {
                    type: "mrkdwn",
                    text: `*Email:*\n${email.toLowerCase().trim()}`,
                  },
                  {
                    type: "mrkdwn",
                    text: `*Source:*\nlanding_page_footer`,
                  },
                ],
              },
              ...(utmSource || utmMedium || utmCampaign
                ? [
                    {
                      type: "section",
                      fields: [
                        ...(utmSource
                          ? [{ type: "mrkdwn", text: `*UTM Source:*\n${utmSource}` }]
                          : []),
                        ...(utmMedium
                          ? [{ type: "mrkdwn", text: `*UTM Medium:*\n${utmMedium}` }]
                          : []),
                        ...(utmCampaign
                          ? [{ type: "mrkdwn", text: `*UTM Campaign:*\n${utmCampaign}` }]
                          : []),
                      ],
                    },
                  ]
                : []),
              {
                type: "context",
                elements: [
                  {
                    type: "mrkdwn",
                    text: `Subscribed at ${new Date().toLocaleString()}`,
                  },
                ],
              },
            ],
          };

          await fetch(slackWebhookUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(slackPayload),
          });
        } catch (slackError) {
          // Don't fail the subscription if Slack notification fails
          console.error("Slack notification error:", slackError);
        }
      }

      setIsSuccess(true);
      return { success: true };
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "An unexpected error occurred.";
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setIsLoading(false);
    setIsSuccess(false);
    setError(null);
  };

  return {
    subscribe,
    isLoading,
    isSuccess,
    error,
    reset,
  };
}
